/**
 * @module app.cm
 */

import { autoinject } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { ITasksConfig, IQueuesConfig, IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { TasksGrid } from 'app/core/components/tasks/tasks-grid';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { CaseChangeEvent } from 'app/cm/lib/events';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class CaseTasksPage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected caseId!: string;
    protected readonly: boolean = false;
    protected includeInactive: boolean = false;

    protected tasksConfig: ITasksConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        taskDetailRouteName: 'cm.case.task',
        backRouteName: 'cm.case.tasks',
        addTargetProperty: this.addCaseProperty.bind(this),
        primaryTargetType: 'CM.CaseTask',
        labelKey: 'core:task.$',
        allowDuplicates: false
    };

    protected queuesConfig: IQueuesConfig = {
        serviceRootUrl: 'cm/tasks',
        targetType: 'CM.Intervention'
    };

    protected assessmentsConfig: IAssessmentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
    };

    private subscriptions: Array<Subscription> = [];

    ////****************** Element References ***************************************////
    protected $grid!: TasksGrid;

    ////****************** Lifecycle events (in order of execution) *****************////
    //TODO: Get rid of event aggregator if possible
    constructor(
        private readonly events: EventAggregator,
        private readonly context: CasePageContext,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { caseId: string }): Promise<boolean> {
        if(!params.caseId) { throw new Error('Missing or invalid caseId parameter'); }

        return true;
    }

    protected async activate(params: { caseId: string }): Promise<void> {
        if(this.caseId !== params.caseId) { this.caseId = params.caseId; }

        await this.accountContext.load();
        const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');

        if(this.context && this.context.case) {
            this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
        }

        this.tasksConfig.allowDuplicates = !!getApiSetting(this.context.settings, 'tasks.$allowDuplicates');
        this.tasksConfig.memberId = this.context.case?.member?.id || undefined;

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {

            await this.accountContext.load();
            const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
            this.readonly = this.context.caseIsReadOnly(canEditClosedCase);
            this.reload();
        };
    }


    ////****************** Functions ************************************************////
    protected async reload(): Promise<void> {
        if(this.$grid && this.caseId) {
            this.$grid.deferReload();
        }
    }

    protected addCaseProperty(model: CM.CaseTask): void {
        model.case = { id: this.caseId };
    }


    ////****************** Service Calls ********************************************////

}
